import { type RequiredChildrenProp, useDebug } from "@huub-web/ui";
import { type FC, useState, useEffect, useMemo, useCallback } from "react";
import {
  type Appointment,
  AppointmentsContext,
  type AppointmentsContextState,
} from "@huub-web/advisor";
import { generateUuid } from "@huub-web/utils";

import { useAppController } from "../../hooks/useAppController";

type AppointmentsProviderProps = RequiredChildrenProp;

const mockAppointment = (name: string): Appointment => ({
  id: generateUuid(),
  date: (() => {
    const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000);
    tomorrow.setHours(11, 0, 0, 0); // Set time to 11:00 AM
    return tomorrow.toISOString();
  })(),
  title: "Hypotheekgesprek met onze Huub adviseur",
  type: "advisor",
  status: "confirmed",
  attendees: [
    {
      name: "Adviseur",
      email: "adviseur@huub.nl",
      image: "/assets/images/adviseur.jpg",
    },
    {
      name,
    },
  ],
});

/**
 * Provider component for managing appointments state throughout the application.
 * Now mocked.
 * @todo Unmock
 */
export const AppointmentsProvider: FC<AppointmentsProviderProps> = ({
  children,
}) => {
  const { user, appCustomer } = useAppController();
  const { log } = useDebug();

  const [isVideoCallActive, setIsVideoCallActive] = useState(false);
  const [appointments, setAppointments] = useState<Appointment[]>([]);

  const upcomingAppointment = appointments?.[0] ?? null;

  useEffect(() => {
    if (appointments.length === 0 && user && user.name) {
      setAppointments([mockAppointment(user.name)]);
    }
  }, [user, appointments]);

  const handleBookAppointment = useCallback<
    AppointmentsContextState["bookAppointment"]
  >(
    (date, onSuccess) => {
      const newAppointment: Appointment = {
        id: generateUuid(),
        date: date.toISOString(),
        title: "Hypotheekgesprek met onze Huub adviseur",
        type: "advisor",
        status: "confirmed",
        attendees: [
          {
            name: "Adviseur",
            email: "adviseur@huub.nl",
            image: "/assets/images/adviseur.jpg",
          },
          {
            name: user.name,
            email: user.email,
          },
        ],
      };

      log(
        "cyan",
        "AppointmentsProvider",
        "newAppointment",
        newAppointment,
        user,
        appCustomer,
      );

      setTimeout(() => {
        setAppointments((prev) => [...prev, newAppointment]);
        onSuccess?.();
      }, 1000);
    },
    [user, log, appCustomer],
  );

  const context = useMemo<AppointmentsContextState>(
    () => ({
      appointments,
      upcomingAppointment,
      availableAdvisor: {
        name: "Adviseur",
        image: "/assets/images/adviseur.jpg",
      },
      isVideoCallActive,
      startVideoCall: () => setIsVideoCallActive(true),
      endVideoCall: () => setIsVideoCallActive(false),
      bookAppointment: handleBookAppointment,
    }),
    [
      appointments,
      upcomingAppointment,
      isVideoCallActive,
      handleBookAppointment,
    ],
  );

  return (
    <AppointmentsContext.Provider value={context}>
      {children}
    </AppointmentsContext.Provider>
  );
};
